import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Crown, Eye, Infinity as InfinityIcon, Filter, Rocket, Check } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  { icon: Eye, title: "See who liked you", body: "Skip the guesswork — every incoming like is visible in one tap." },
  { icon: InfinityIcon, title: "Unlimited likes", body: "Free members get 10 likes/day. Premium removes the cap entirely." },
  { icon: Filter, title: "Advanced filters", body: "Filter Discover by age range, HSV status, and what people are looking for." },
  { icon: Rocket, title: "Monthly boost", body: "Float to the top of everyone's Discover for 30 minutes, once a month." },
];

// TEMPORARY: paid Stripe checkout is paused while Emergent Support finishes the
// runtime env cleanup for the retired managed Stripe sandbox (job e1cf2908-...).
// Founding Members (first 50) are unaffected — they get Premium automatically at
// signup without ever hitting Stripe. Flip this flag back to `false` once
// support confirms `sk_live_...` reaches the deployed container and
// live Products/Prices exist in the real Stripe account.
const PAYMENTS_DISABLED = false;
// Show PayPal buttons alongside Stripe. Hidden when the user is already a
// Founding Member or already Premium. Reads status.paypal_enabled from the
// backend (true when PAYPAL_CLIENT_ID is configured in the pod env).
const PAYPAL_ENABLED = true;

export default function Premium() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoadError(false);
    try {
      const { data } = await api.get("/premium/status");
      setStatus(data);
    } catch {
      setLoadError(true);
    } finally {
      setPageLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const subscribeWithPaypal = async (plan) => {
    setLoading(true);
    try {
      const lookup_key = plan === "monthly" ? "premium_monthly" : "premium_yearly";
      const { data } = await api.post("/payments/paypal/create-subscription", {
        lookup_key,
        origin_url: window.location.origin,
      });
      if (data?.approve_url) {
        window.location.href = data.approve_url;
        return;
      }
      toast.error("Couldn't start PayPal checkout. Try again.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start PayPal checkout. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel Premium? Your Premium features stay active until your current billing period ends.")) return;
    setLoading(true);
    try {
      await api.post("/premium/cancel");
      toast.success("Premium cancelled");
      const { data: me } = await api.get("/auth/me");
      setUser(me);
      await load();
    } catch {
      toast.error("Couldn't cancel");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[#FFFDF9]">
        <Navbar />
        <main className="max-w-5xl mx-auto px-4 py-16 text-center text-[#6E655F]" data-testid="premium-loading">
          Loading Premium…
        </main>
      </div>
    );
  }

  if (loadError || !status) {
    return (
      <div className="min-h-screen bg-[#FFFDF9]">
        <Navbar />
        <main className="max-w-md mx-auto px-4 py-16 text-center" data-testid="premium-load-error">
          <p className="text-[#2C2623] font-serif text-xl mb-2">Couldn't load Premium</p>
          <p className="text-sm text-[#6E655F] mb-4">Please check your connection and try again.</p>
          <Button onClick={load} className="rounded-full bg-[#D95D39] hover:bg-[#C24C29]">Retry</Button>
        </main>
      </div>
    );
  }

  const monthlyPrice = status.pricing.monthly.price_usd;
  const yearlyPrice = status.pricing.yearly.price_usd;
  const yearlySavingsPct = Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100);

  return (
    <div className="min-h-screen bg-[#FFFDF9]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FEF3D6] text-[#B45309] text-sm font-medium mb-4">
            <Crown className="w-4 h-4" /> HerpMates Premium
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#2C2623] tracking-tight">
            Meet more of the right people.
          </h1>
          <p className="text-[#6E655F] mt-3 max-w-xl mx-auto">
            Signup and everyday connection are always free. Premium unlocks the perks below —
            for members who want a little extra help finding their person.
          </p>
        </div>

        {status.is_founding_member && (
          <Card className="p-6 mb-8 rounded-3xl border-[#E09F3E] bg-gradient-to-r from-[#FEF3D6] to-[#FFF0EC]"
            data-testid="premium-founding-card">
            <div className="flex items-start gap-3">
              <Crown className="w-6 h-6 text-[#B45309] mt-1" />
              <div className="flex-1">
                <div className="font-serif text-lg font-bold text-[#2C2623]">You're a Founding Member ✨</div>
                <div className="text-sm text-[#6E655F]">
                  Premium for life — no charges, ever. Thanks for helping build HerpMates.
                </div>
              </div>
            </div>
          </Card>
        )}

        {status.is_premium && !status.is_founding_member && (
          <Card className="p-6 mb-8 rounded-3xl border-[#F5D77E] bg-[#FEF3D6]/50" data-testid="premium-active-card">
            <div className="flex items-start gap-3">
              <Crown className="w-6 h-6 text-[#B45309] mt-1" />
              <div className="flex-1">
                <div className="font-semibold text-[#2C2623]">You're a Premium member</div>
                <div className="text-sm text-[#6E655F]">
                  Plan: <b>{status.plan}</b> · {status.cancel_at_period_end ? "cancelled, access ends" : "renews"}{" "}
                  {status.premium_expires_at ? new Date(status.premium_expires_at).toLocaleDateString() : "soon"}
                </div>
              </div>
              {!status.cancel_at_period_end && (
              <Button variant="outline" onClick={cancel} disabled={loading}
                data-testid="premium-cancel-btn"
                className="rounded-full border-[#E8DEC8]">
                Cancel Premium
              </Button>
              )}
            </div>
          </Card>
        )}

        {/* Feature grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <Card key={title} className="p-6 rounded-2xl border-[#E8DEC8] bg-white">
              <div className="w-11 h-11 rounded-full bg-[#FFF0EC] flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-[#D95D39]" />
              </div>
              <h3 className="font-serif text-xl font-semibold text-[#2C2623] mb-1">{title}</h3>
              <p className="text-sm text-[#6E655F] leading-relaxed">{body}</p>
            </Card>
          ))}
        </div>

        {/* Plans — hidden entirely for Founding Members (lifetime Premium, nothing to buy). */}
        {!status.is_founding_member && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="premium-plans-grid">
          <Card className="p-8 rounded-3xl border-[#E8DEC8] bg-white" data-testid="plan-monthly">
            <div className="text-sm uppercase tracking-widest text-[#6E655F] font-semibold">Monthly</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-serif text-5xl font-bold text-[#2C2623]">${monthlyPrice.toFixed(2)}</span>
              <span className="text-[#6E655F]">/month</span>
            </div>
            <ul className="mt-6 space-y-2 text-sm text-[#2C2623]">
              {FEATURES.map((f) => (
                <li key={f.title} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#2A524A]" /> {f.title}
                </li>
              ))}
              <li className="flex items-center gap-2 text-[#6E655F]">
                <Check className="w-4 h-4 text-[#6E655F]" /> Cancel anytime
              </li>
            </ul>
          </Card>

          <Card className="p-8 rounded-3xl border-2 border-[#D95D39] bg-white relative" data-testid="plan-yearly">
            <Badge className="absolute top-4 right-4 bg-[#D95D39] hover:bg-[#D95D39]">Save {yearlySavingsPct}%</Badge>
            <div className="text-sm uppercase tracking-widest text-[#B45309] font-semibold">Yearly · best value</div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-serif text-5xl font-bold text-[#2C2623]">${yearlyPrice.toFixed(0)}</span>
              <span className="text-[#6E655F]">/year</span>
            </div>
            <div className="text-xs text-[#6E655F] mt-1">
              ${(yearlyPrice / 12).toFixed(2)}/month billed yearly
            </div>
            <ul className="mt-6 space-y-2 text-sm text-[#2C2623]">
              {FEATURES.map((f) => (
                <li key={f.title} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-[#2A524A]" /> {f.title}
                </li>
              ))}
              <li className="flex items-center gap-2 text-[#2A524A] font-semibold">
                <Check className="w-4 h-4" /> {yearlySavingsPct}% off vs monthly
              </li>
            </ul>
          </Card>
        </div>
        )}

        {status.paypal_enabled && PAYPAL_ENABLED && !status.is_founding_member && !(status.is_premium) && (
        <div className="mt-10" data-testid="paypal-section">
          <div className="text-center mb-5">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#003087]/10 text-[#003087] text-xs font-semibold uppercase tracking-widest">
              PayPal
            </div>
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#2C2623] dark:text-[#FAF5F0] mt-3">
              Subscribe securely with PayPal
            </h3>
            <p className="text-sm text-[#6E655F] dark:text-[#B5AAA1] mt-2 max-w-md mx-auto">
              Cancel anytime from your PayPal dashboard or your HerpMates profile.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto">
            <Button
              onClick={() => subscribeWithPaypal("monthly")}
              disabled={loading}
              data-testid="paypal-subscribe-monthly-btn"
              className="h-12 rounded-full bg-[#FFC439] hover:bg-[#F0B429] text-[#003087] font-bold">
              Pay with PayPal — Monthly ($19.99)
            </Button>
            <Button
              onClick={() => subscribeWithPaypal("yearly")}
              disabled={loading}
              data-testid="paypal-subscribe-yearly-btn"
              className="h-12 rounded-full bg-[#FFC439] hover:bg-[#F0B429] text-[#003087] font-bold">
              Pay with PayPal — Yearly ($199)
            </Button>
          </div>
        </div>
        )}

        {!status.is_founding_member && PAYMENTS_DISABLED && (
        <p className="text-center text-sm text-[#6E655F] mt-6 max-w-lg mx-auto leading-relaxed" data-testid="premium-payments-paused-notice">
          We're finalizing payments — Premium plans will be available very soon.
          In the meantime, the first 50 registered members automatically get
          <b className="text-[#2C2623]"> Premium free for life</b>. Everything else in HerpMates
          keeps working normally.
        </p>
        )}
      </main>
    </div>
  );
}
